import React, { useEffect, useState, useRef, useCallback } from 'react';
import lodashGet from 'lodash/get';
import { useLocation } from '@reach/router';
import { apiHolder, apis } from 'api';
import debounce from 'lodash/debounce';
import { judgeMsgType } from '@web-im/utils/im_team_util';
import { reduce, initState, MessageAction, FileMessageCustomEvent, MESSAGE_LIMIT } from './messagesReduce';
import { getParams } from '../../common/navigate';
import { MsgSubtypes } from './msgSubtypes';
import { getIn18Text } from 'api';
export { MessageAction, FileMessageCustomEvent } from './messagesReduce';
const getFileDesc = (param) => {
    const typeDesc = {
        image: '[图片]',
        video: '[视频]',
        audio: '[音频]',
        file: '[文件]',
    };
    if (param.type !== 'file') {
        return typeDesc[param.type];
    }
    return `${typeDesc[param.type]}${param.customFile.name}`;
};
export const Context = React.createContext({});
const nimApi = apiHolder.api.requireLogicalApi('NIM');
const systemApi = apiHolder.api.getSystemApi();
const discussApi = apiHolder.api.requireLogicalApi(apis.imDiscussApiImpl);
const createProgressFunction = function (eventName, customIdClient) {
    return (...args) => {
        nimApi.emitCustomEvent(eventName, customIdClient, ...args);
    };
};
export const Provider = props => {
    const [state, dispatch] = React.useReducer(reduce, initState);
    const [newMsgs, setNewMsgs] = useState([]);
    const [selecting, setSelecting] = useState(false);
    const [selectList, setSelectList] = useState([]);
    const [mailListCount, setMailListCount] = useState(0);
    const [mailList, setMailList] = useState([]);
    const location = useLocation();
    const curSessionId = getParams(location.hash, 'sessionId');
    const curMode = getParams(location.hash, 'mode');
    const isMountedRef = useRef(true);
    const mergeMsgs = useCallback(async () => {
        const unMergedMsgs = newMsgs.filter(item => item.sessionId === curSessionId);
        const mergedMsgs = nimApi.mergeMsgs(state.msgList, unMergedMsgs);
        dispatch({
            type: MessageAction.PREPEND_MSGS,
            payload: {
                msgList: mergedMsgs,
            },
        });
        setNewMsgs([]);
    }, [newMsgs.length]);
    useEffect(() => {
        if (!newMsgs.length) {
            return;
        }
        mergeMsgs();
    }, [newMsgs.length]);
    const getMailList = async () => {
        var _a;
        try {
            const { success, data } = await discussApi.getDiscussMail({ teamId: curSessionId ? (_a = curSessionId.split('-')[1]) !== null && _a !== void 0 ? _a : '' : '' });
            if (success && data != null) {
                const { total, msgs } = data;
                setMailListCount(total);
                setMailList(msgs);
            }
        }
        catch (err) { }
    };
    const deleteLocalMsg = async (msg, isDispatch = true) => {
        try {
            await nimApi.excute('deleteMsgSelf', { msg });
        }
        catch (ex) {
            nimApi.excute('deleteLocalMsg', { msg });
        }
        if (isDispatch) {
            dispatch({
                type: MessageAction.DELETE_MSG,
                payload: {
                    idClient: msg.idClient,
                },
            });
        }
    };
    let newMsgList = [];
    const debounceSetNewMsgs = debounce(() => {
        if (newMsgList.length > 0) {
            setNewMsgs(state => [...state, ...newMsgList]);
            newMsgList = [];
        }
    }, 20);
    const sendBasicMessage = async (methodName, options, mapFunc) => new Promise(async (resolve, reject) => {
        const msg = await nimApi.excute(methodName, Object.assign({ needMsgReceipt: true, isPushable: false, needReturn: true, done: async (error, result) => {
                newMsgList.push(typeof mapFunc === 'function' ? mapFunc(result) : result);
                debounceSetNewMsgs();
                if (error instanceof Error) {
                    reject(error);
                }
                else {
                    resolve(result);
                }
            } }, options));
        if (lodashGet(msg, 'idClient.length', 0) !== 0) {
            newMsgList.push(typeof mapFunc === 'function' ? mapFunc(msg) : msg);
            debounceSetNewMsgs();
        }
    });
    const sendCustomMessage = async (options) => sendBasicMessage('sendCustomMsg', options);
    const sendTextMessage = async (options) => sendBasicMessage('sendText', options);
    const sendFileMessage = async (options) => {
        const typeMap = {
            image: 'LOCAL_UPLOADING_IMG',
            video: 'LOCAL_UPLOADING_VIDEO',
        };
        const customKeyId = systemApi.md5(`${Math.random()}`);
        const localCustomMsg = await sendBasicMessage('sendCustomMsg', Object.assign(Object.assign({}, options), { idClient: customKeyId, isLocal: true, content: JSON.stringify({
                type: typeMap[options.type] || 'LOCAL_UPLOADING_FILE',
                data: options.customFile,
            }) }));
        const file = await nimApi.excute('previewFile', {
            type: options.type,
            blob: options.blob,
            beginupload: createProgressFunction(FileMessageCustomEvent.BEGIN_UPLOAD, customKeyId),
            uploadprogress: createProgressFunction(FileMessageCustomEvent.UPLOAD_PROGRESS, customKeyId),
        });
        const { msg } = (await nimApi.excute('getLocalMsgByIdClient', {
            idClient: localCustomMsg.idClient,
        }));
        if (!msg) {
            return Promise.resolve();
        }
        const replyOption = {};
        if (lodashGet(msg, 'replyMsgIdClient.length', 0) !== 0) {
            const { msg: replyMsg } = (await nimApi.excute('getLocalMsgByIdClient', {
                idClient: msg.replyMsgIdClient,
            }));
            replyOption.replyMsg = replyMsg;
        }
        await deleteLocalMsg(localCustomMsg);
        ['fileInput', 'filePath', 'blob'].forEach(key => {
            Reflect.deleteProperty(options, key);
        });
        return sendBasicMessage('sendFile', Object.assign(Object.assign({ file, text: getFileDesc(options) }, options), replyOption), state => {
            if (options.type === 'image') {
                state.localUrl = options.customFile.url;
            }
            return state;
        });
    };
    const fetchLocalMsg = async (options = {}, requestRemote = false) => {
        options = Object.assign({ sessionId: curSessionId, desc: true, limit: MESSAGE_LIMIT }, options);
        const results = await nimApi.excute('getLocalMsgs', options);
        let hasRetryHistoryMode = false;
        if (results.msgs.length < options.limit && requestRemote) {
            const [scene, to] = curSessionId.split('-');
            const len = Math.max(options.limit - results.msgs.length, 1);
            hasRetryHistoryMode = true;
            const getHistoryOptions = {
                scene,
                to,
                [Number.isSafeInteger(options.end) ? 'endTime' : 'myEndTime']: options.end,
                [Number.isSafeInteger(options.start) ? 'beginTime' : 'myBeginTime']: options.start,
                limit: len,
                reverse: !options.desc,
            };
            const historyMsgs = await nimApi.excute('getHistoryMsgs', getHistoryOptions);
            results.msgs = await nimApi.mergeMsgs(results.msgs, historyMsgs.msgs);
            results.msgs = results.msgs.filter(msg => {
                const shouldIgnore = lodashGet(msg, 'attach.type', '') === 'updateTeam' && lodashGet(msg, 'attach.team.serverCustom', '');
                const shouldRemoveMember = lodashGet(msg, 'attach.type', '') === 'removeTeamMembers';
                return !shouldIgnore && !shouldRemoveMember;
            });
        }
        if (!hasRetryHistoryMode &&
            !options.desc &&
            lodashGet(results, 'msgs.length', 0) !== 0 &&
            Number.isSafeInteger(options.start) &&
            lodashGet(results, 'msgs[0].time', 0) !== options.start) {
            const [scene, to] = curSessionId.split('-');
            const getHistoryOptions = {
                scene,
                to,
                beginTime: options.start,
                limit: options.limit,
                reverse: true,
            };
            const historyMsgs = await nimApi.excute('getHistoryMsgs', getHistoryOptions);
            results.msgs = historyMsgs.msgs.filter(msg => {
                const shouldIgnore = lodashGet(msg, 'attach.type', '') === 'updateTeam' && lodashGet(msg, 'attach.team.serverCustom', '');
                const shouldRemoveMember = lodashGet(msg, 'attach.type', '') === 'removeTeamMembers';
                return !shouldIgnore && !shouldRemoveMember;
            });
        }
        return results.msgs.map(item => {
            item.sourceType = 'pull';
            return item;
        });
    };
    useEffect(() => {
        if (!isMountedRef.current) {
            isMountedRef.current = true;
        }
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    const getMoreMsgs = async (options, isFetchHistory = false) => {
        let results = [];
        if (lodashGet(options, 'limit', MESSAGE_LIMIT) > 100) {
            let limit = lodashGet(options, 'limit', MESSAGE_LIMIT);
            let promise = Promise.resolve([]);
            while (limit >= 0) {
                const unitLimit = Math.min(limit, MESSAGE_LIMIT);
                promise = promise.then(async (list) => {
                    let remainList = [];
                    remainList = await fetchLocalMsg(Object.assign(Object.assign({}, options), { limit: unitLimit, [options.desc === false ? 'start' : 'end']: lodashGet(list, `${list.length - 1}.time`, options.desc === false ? 0 : Infinity) }), isFetchHistory);
                    return [...list, ...remainList];
                });
                limit -= MESSAGE_LIMIT;
            }
            results = await promise;
        }
        else {
            results = await fetchLocalMsg(options, isFetchHistory);
        }
        if (isMountedRef.current) {
            setNewMsgs(state => [...state, ...results]);
        }
        return Promise.resolve(results);
    };
    const updateSelectState = (sessionId) => {
        if (!sessionId) {
            setSelectList([]);
            setSelecting(false);
            return;
        }
        setSelectList([sessionId]);
        setSelecting(true);
    };
    const setSelectMsgs = (msgIdClient) => {
        if (selectList.includes(msgIdClient)) {
            setSelectList(pre => pre.filter(item => item !== msgIdClient));
        }
        else {
            const newSelectList = [...selectList];
            newSelectList.push(msgIdClient);
            setSelectList(newSelectList);
        }
    };
    const getSelectMsgs = () => {
        if (!selecting) {
            return null;
        }
        return selectList;
    };
    const drawMsg = async (msg, isTeamDiscuss) => {
        var _a, _b;
        if (isTeamDiscuss && judgeMsgType(msg, 'type', 1014) && judgeMsgType(msg, 'customMsgType', 2000)) {
            const params = {
                teamId: (msg === null || msg === void 0 ? void 0 : msg.to) || '',
                msgId: (msg === null || msg === void 0 ? void 0 : msg.idServer) || '',
            };
            const cancelRes = await discussApi.cancelDiscussBind(params);
            if (!((_a = cancelRes === null || cancelRes === void 0 ? void 0 : cancelRes.data) === null || _a === void 0 ? void 0 : _a.result)) {
                throw Error(getIn18Text('XIAOXICHEHUISHI'));
            }
        }
        await nimApi.excute('deleteMsg', {
            msg,
        });
        let accounts = [];
        if (Reflect.has(msg, 'apns') && lodashGet(msg, 'apns.accounts.length', 0) === 0) {
            accounts.push('all');
            try {
                const { mentions } = JSON.parse(msg.custom);
                accounts = [...accounts, ...mentions];
            }
            catch (ex) { }
        }
        else if (Reflect.has(msg, 'apns') && lodashGet(msg, 'apns.accounts.length', 0) !== 0) {
            accounts = [...accounts, ...(_b = msg.apns) === null || _b === void 0 ? void 0 : _b.accounts];
        }
        const customDrawedInfo = msg.type === 'text'
            ? {
                drawedMsgText: msg.text,
                drawedMsgAccounts: accounts,
                drawedTimestmap: new Date().getTime(),
            }
            : {};
        sendBasicMessage('sendTipMsg', {
            scene: msg.scene === 'p2p' ? 'p2p' : 'team',
            idClient: msg.idClient,
            custom: Object.assign({}, customDrawedInfo),
            subType: msg.type === 'text' ? MsgSubtypes.DRAW_TEXT_MSG : MsgSubtypes.DRAW_MSG,
            localFrom: msg.from,
            time: msg.time,
            to: msg.to,
            tip: getIn18Text('CHEHUILEYITIAO'),
            isLocal: true,
        });
    };
    const onmsg = msg => {
        if (curSessionId !== msg.sessionId && curMode !== 'normal') {
            return;
        }
        newMsgList.push(msg);
        debounceSetNewMsgs();
    };
    const appendDeleteMsg = async (msg) => {
        const localMsg = await nimApi.excute('sendTipMsg', {
            scene: msg.scene,
            to: msg.to,
            custom: {},
            tip: getIn18Text('CHEHUILEYITIAO'),
            localFrom: msg.from,
            time: msg.time,
            isLocal: true,
            idClient: msg.idClient,
            subType: MsgSubtypes.DRAWN_MSG,
        });
        if (curSessionId === msg.sessionId && curMode === 'normal') {
            newMsgList.push(localMsg);
            debounceSetNewMsgs();
        }
        if (selectList.length > 0 && selectList.includes(msg.idClient)) {
            setSelectList(pre => pre.filter(item => item !== msg.idClient));
        }
    };
    const onsysmsg = async (sysMsg) => {
        const { type, msg } = sysMsg;
        switch (type) {
            case 'deleteMsg':
                appendDeleteMsg(msg);
                break;
            default:
                break;
        }
    };
    const [$t, setRepullHandle] = useState(false);
    const onconnect = () => {
        if (curMode !== 'normal') {
            return;
        }
        let count = 0;
        const _t = setInterval(() => {
            if (count >= 3) {
                clearInterval(_t);
            }
            getMoreMsgs({
                sessionId: curSessionId,
                end: Infinity,
            });
            count += 1;
        }, 2000);
        setRepullHandle(_t);
    };
    const insertRemoveMemberMsg = async (params) => {
        const { from, fromNick, tid: teamId, yxIdToNickName } = typeof params === 'string' ? JSON.parse(params) : params;
        const removeNicks = Object.values(yxIdToNickName);
        const removeAccounts = Object.keys(yxIdToNickName);
        if (curSessionId !== `team-${teamId}` || curMode !== 'normal') {
            return;
        }
        const msg = await nimApi.excute('sendTipMsg', {
            scene: 'team',
            to: teamId,
            custom: {
                from,
                removeAccounts,
                fromNick: fromNick,
                removeNicks,
            },
            isLocal: true,
            tip: `${fromNick}将${removeNicks.join('、')} 移出群组`,
            subType: MsgSubtypes.REMOVE_TEAMMEBER,
        });
        newMsgList.push(msg);
        debounceSetNewMsgs();
    };
    const oncustomsysmsg = (sysContent) => {
        let subType = '';
        let parsedContent = {
            subType: '',
            data: undefined,
        };
        try {
            parsedContent = JSON.parse(sysContent.content);
            subType = parsedContent.subType;
        }
        catch (ex) { }
        switch (subType) {
            case 'mail_msg':
                getMailList();
                break;
            case 'team_kick': {
                insertRemoveMemberMsg(parsedContent.data);
                break;
            }
            default:
                break;
        }
    };
    useEffect(() => () => {
        $t && clearInterval($t);
    }, []);
    useEffect(() => {
        if (curMode === 'normal') {
            nimApi.subscrible('onmsg', onmsg);
            nimApi.subscrible('onconnect', onconnect);
        }
        nimApi.subscrible('oncustomsysmsg', oncustomsysmsg);
        return () => {
            nimApi.unSubcrible('onmsg', onmsg);
            nimApi.unSubcrible('onconnect', onconnect);
            nimApi.unSubcrible('oncustomsysmsg', oncustomsysmsg);
        };
    }, []);
    useEffect(() => {
        if (curMode === 'normal') {
            nimApi.subscrible('onsysmsg', onsysmsg);
        }
        return () => {
            nimApi.unSubcrible('onsysmsg', onsysmsg);
        };
    }, [selectList]);
    const updateLocalMsg = (msg, key, value) => {
        let localCustom = {};
        try {
            localCustom = JSON.parse(msg.localCustom || '');
        }
        catch (ex) { }
        nimApi.excute('updateLocalMsg', {
            idClient: msg.idClient,
            localCustom: JSON.stringify(Object.assign(Object.assign({}, localCustom), { [key]: value })),
        });
    };
    useEffect(() => {
        let isMounted = true;
        nimApi
            .excute('getLocalMsgs', {
            desc: false,
            limit: 1,
        })
            .then(res => {
            if (!isMounted)
                return;
            const oldestMsgTime = lodashGet(res, 'msgs[0].time');
            dispatch({
                type: MessageAction.UPDATE_OLDEST_MSGTIME,
                payload: {
                    timestamp: oldestMsgTime,
                },
            });
        });
        return () => {
            isMounted = false;
        };
    }, []);
    return (React.createElement(Context.Provider, { value: {
            state,
            dispatch,
            getMoreMsgs,
            sendTextMessage,
            sendCustomMessage,
            sendFileMessage,
            drawMsg,
            updateLocalMsg,
            deleteLocalMsg,
            updateSelectState,
            setSelectMsgs,
            getSelectMsgs,
            getMailList,
            mailListCount,
            mailList,
        } }, props.children));
};
